import type { ApiResponseBody } from "@/lib/api/api-type-helper";
import { apiClient } from "@/lib/api/client";
import { getDataOrThrowApiError } from "@/lib/api/error";

export type OwnerProduct =
  | "HRF_LABOR_CORE"
  | "HRF_CONTRACTOR_CORE"
  | "IT_OPERATION_CORE"
  | "GA_OPERATION_CORE";

// --- Get Operation Packages ---

export type OperationPackagesResponse = ApiResponseBody<
  "/v2/boe/packages/{ownerProduct}",
  "get"
>;

export type PackageType = OperationPackagesResponse[number]["packageType"];

export const getOperationPackages = async (
  ownerProduct: OwnerProduct,
): Promise<OperationPackagesResponse> => {
  const res = await apiClient.GET("/v2/boe/packages/{ownerProduct}", {
    params: {
      path: { ownerProduct },
    },
  });
  return getDataOrThrowApiError(res);
};

// --- Get Operation Package Count In Progress ---

export type OperationPackageCountInProgressResponse = ApiResponseBody<
  "/v2/boe/packages/{ownerProduct}/{id}/count-in-progress",
  "get"
>;

export const getOperationPackageCountInProgress = async (
  ownerProduct: OwnerProduct,
  id: string,
): Promise<OperationPackageCountInProgressResponse> => {
  const res = await apiClient.GET(
    "/v2/boe/packages/{ownerProduct}/{id}/count-in-progress",
    {
      params: {
        path: { ownerProduct, id },
      },
    },
  );
  return getDataOrThrowApiError(res);
};

// --- Get Operation Package Patterns ---

export type OperationPackagePatternsResponse = ApiResponseBody<
  "/v2/boe/packages/{ownerProduct}/{id}/patterns",
  "get"
>;

export const getOperationPackagePatterns = async (
  ownerProduct: OwnerProduct,
  id: string,
): Promise<OperationPackagePatternsResponse> => {
  const res = await apiClient.GET(
    "/v2/boe/packages/{ownerProduct}/{id}/patterns",
    {
      params: {
        path: { ownerProduct, id },
      },
    },
  );
  return getDataOrThrowApiError(res);
};

// --- Post Operation Package Patterns ---

export type PostOperationPackagePatternsResponse = ApiResponseBody<
  "/v2/boe/packages/{ownerProduct}/{id}/patterns",
  "post"
>;

export const postOperationPackagePatterns = async (
  ownerProduct: OwnerProduct,
  id: string,
  body?: { name: string; description?: string },
): Promise<PostOperationPackagePatternsResponse> => {
  const res = await apiClient.POST(
    "/v2/boe/packages/{ownerProduct}/{id}/patterns",
    {
      params: {
        path: { ownerProduct, id },
      },
      body: body ?? {},
    },
  );
  return getDataOrThrowApiError(res);
};

// --- Assign Task To Person ---

export type StartPatternResponse = ApiResponseBody<
  "/v2/boe/packages/{ownerProduct}/{id}/patterns/{patternId}",
  "post"
>;

export type StartPatternRequestBody = {
  targetPersonIds: string[];
  anchorDate: string;
};

export const startPattern = async (
  ownerProduct: OwnerProduct,
  id: string,
  patternId: string,
  body: StartPatternRequestBody,
): Promise<StartPatternResponse> => {
  const res = await apiClient.POST(
    "/v2/boe/packages/{ownerProduct}/{id}/patterns/{patternId}",
    {
      params: {
        path: { ownerProduct, id, patternId },
      },
      body,
    },
  );
  return getDataOrThrowApiError(res);
};

// --- Get Operation Task Count In Progress ---

export type OperationTaskCountInProgressResponse = ApiResponseBody<
  "/v2/boe/packages/{ownerProduct}/{id}/patterns/{patternId}/count-in-progress",
  "get"
>;

export const getOperationTaskCountInProgress = async (
  ownerProduct: OwnerProduct,
  id: string,
  patternId: string,
): Promise<OperationTaskCountInProgressResponse> => {
  const res = await apiClient.GET(
    "/v2/boe/packages/{ownerProduct}/{id}/patterns/{patternId}/count-in-progress",
    {
      params: {
        path: { ownerProduct, id, patternId },
      },
    },
  );
  return getDataOrThrowApiError(res);
};

// --- Get Operation Package Instances ---

export type OperationPackageInstancesResponse = ApiResponseBody<
  "/v2/boe/packages/{ownerProduct}/instances",
  "get"
>;

export const getOperationPackageInstances = async (
  ownerProduct: OwnerProduct,
  patternId: string,
): Promise<OperationPackageInstancesResponse> => {
  const res = await apiClient.GET("/v2/boe/packages/{ownerProduct}/instances", {
    params: {
      path: { ownerProduct },
      query: { patternId },
    },
  });
  return getDataOrThrowApiError(res);
};
